import React, { useEffect, useMemo, useState } from "react";
import {
  X, UserPlus, Loader2, GraduationCap,
  Phone, User, Save,
  Heart, ShieldCheck, MapPin
} from "lucide-react";
import Select from "react-select";
import { AnimatePresence, motion } from "framer-motion";

import {
  createStudent,
  updateStudent,
  getStudentMetaSchools,
  getStudentMetaClasses,
} from "../../../api/elearning/students/students";
import { getAllBranches_Array, getBranchById } from "../../../api/hrm/branches/info";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../components/ConfirmDialog";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import {
  customerInModalSelectStyles,
  formatCustomerOptionLabel,
  filterCustomerOption,
  modalSelectPortalProps,
} from "../../../components/ui/customerSelectInModal";
import {
  classSelectStyles,
  classSelectPortalProps,
  formatClassOptionLabel,
  filterClassOption,
  mapClassesToSelectOptions,
} from "../../../components/ui/classSelectInModal";
import HrmDashboardBranchPicker, {
  branchSubtitle,
} from "../../hrm/shared/hrmDashboardBranchSelect";
import { toTitleCaseVietnamese } from "../../../utils/textUtils";

const STUDENT_CODE_RE = /^\d{10}$/;
const SESSION_SUPER_ROLES = [
  "super admin",
  "superadmin",
  "administrator",
  "system administrator",
];

function readSessionUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

function readUserBranchId() {
  const u = readSessionUser();
  return u.branch?.id || u.branch_id || null;
}

function isSuperAdminSession() {
  const u = readSessionUser();
  const roles = Array.isArray(u.roles) ? u.roles : [];
  return roles
    .map((r) => String(r || "").trim().toLowerCase())
    .some((r) => SESSION_SUPER_ROLES.includes(r));
}

/* ================= COMPONENT PHỤ: SECTION ================= */
const Section = ({ icon: Icon, title, subTitle, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm space-y-4">
    <div className="flex items-center gap-3 pb-2 border-b border-gray-50">
      <div className="p-2 bg-red-50 rounded-lg">
        <Icon className="w-5 h-5 text-red-600" />
      </div>
      <div>
        <h3 className="text-sm font-bold text-gray-800 uppercase tracking-tight">{title}</h3>
        {subTitle && <p className="text-xs text-gray-400">{subTitle}</p>}
      </div>
    </div>
    <div className="pt-2">{children}</div>
  </div>
);

const StudentFormModal = ({
  isOpen,
  onClose,
  onSaved,
  mode = "create",
  editingStudent = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [studentCode, setStudentCode] = useState("");
  const [fullName, setFullName] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [branchId, setBranchId] = useState(null);
  const [branchAffiliation, setBranchAffiliation] = useState("affiliated");
  const [branchOptions, setBranchOptions] = useState([]);
  const [unit, setUnit] = useState("center");
  const [schoolId, setSchoolId] = useState("");
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [parentName, setParentName] = useState("");
  const [parentPhone, setParentPhone] = useState("");
  const [classesLoading, setClassesLoading] = useState(false);

  const classOptions = useMemo(
    () => mapClassesToSelectOptions(classes),
    [classes]
  );
  const isFranchiseBranch = branchAffiliation === "franchise";
  const canPickUnit = isSuperAdmin || !isFranchiseBranch;
  const showSchoolSelect = isSuperAdmin && unit === "school";
  const genderOptions = useMemo(
    () => [
      { value: "male", label: "Nam" },
      { value: "female", label: "Nữ" },
    ],
    []
  );
  const genderSelectStyles = useMemo(
    () => ({
      ...customSelectStyles,
      control: (provided, state) => ({
        ...customSelectStyles.control(provided, state),
        minHeight: 42,
        height: 42,
      }),
      valueContainer: (provided) => ({
        ...customSelectStyles.valueContainer(provided),
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 12,
        paddingRight: 8,
      }),
      singleValue: (provided) => ({
        ...customSelectStyles.singleValue(provided),
        fontWeight: 500,
      }),
      indicatorSeparator: () => ({ display: "none" }),
      dropdownIndicator: (provided) => ({
        ...provided,
        paddingTop: 6,
        paddingBottom: 6,
      }),
    }),
    []
  );

  useEffect(() => {
    if (!isOpen) return;
    setClassesLoading(true);
    const init = async () => {
      const superAdmin = isSuperAdminSession();
      setIsSuperAdmin(superAdmin);
      if (superAdmin) {
        try {
          const rows = await getAllBranches_Array({ is_active: true, limit: 500 });
          const opts = (rows || []).map((b) => ({
            value: b.id,
            label: b.name,
            affiliation_type: b.affiliation_type || "affiliated",
            manager: b.manager || "",
          }));
          setBranchOptions(opts);
          setBranchId(null);
          setBranchAffiliation("affiliated");
          setUnit("school");
        } catch {
          setBranchOptions([]);
        } finally {
          setClassesLoading(false);
        }
        return;
      }
      const bid = readUserBranchId();
      setBranchId(bid || null);
      if (!bid) {
        setClassesLoading(false);
        return;
      }
      try {
        const br = await getBranchById(bid);
        const aff = br?.affiliation_type || "affiliated";
        setBranchAffiliation(aff);
        setUnit(aff === "franchise" ? "center" : "school");
      } catch {
        setBranchAffiliation("affiliated");
      } finally {
        setClassesLoading(false);
      }
    };
    init();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!branchId || !isSuperAdmin || unit !== "school") {
      setSchools([]);
      return;
    }
    setClassesLoading(true);
    getStudentMetaSchools(branchId)
      .then((d) => setSchools(d?.schools || []))
      .catch(() => setSchools([]))
      .finally(() => setClassesLoading(false));
  }, [isOpen, branchId, isSuperAdmin, unit]);

  useEffect(() => {
    if (!isOpen) return;
    if (isSuperAdmin && !branchId) {
      setClasses([]);
      return;
    }
    const loadClasses = async () => {
      setClassesLoading(true);
      try {
        const params = { unit: unit === "center" ? "center" : "school" };
        if (isSuperAdmin && branchId) {
          params.branch_id = branchId;
          if (unit === "school" && schoolId) params.school_id = schoolId;
        }
        const d = await getStudentMetaClasses(params);
        setClasses(Array.isArray(d?.classes) ? d.classes : []);
      } catch {
        setClasses([]);
      } finally {
        setClassesLoading(false);
      }
    };
    loadClasses();
  }, [isOpen, branchId, unit, schoolId, isSuperAdmin]);

  /** API danh sách trả `classes[]` (class_id trong phần tử), không có class_id ở root */
  const resolvedEditClassId = useMemo(() => {
    if (!editingStudent) return null;
    if (editingStudent.class_id) return editingStudent.class_id;
    const arr = editingStudent.classes;
    if (Array.isArray(arr) && arr.length > 0 && arr[0]?.class_id) return arr[0].class_id;
    return null;
  }, [editingStudent]);

  useEffect(() => {
    if (!isOpen) return;
    if (mode === "create") resetForm();
    else if (mode === "edit" && editingStudent) {
      setStudentCode(editingStudent.student_code || "");
      setFullName(editingStudent.full_name || "");
      setDob(editingStudent.dob?.slice(0, 10) || "");
      setGender(editingStudent.gender || "");
      setPhone(editingStudent.phone || "");
      setEmail(editingStudent.email || "");
      setAddress(editingStudent.address || "");
      setParentName(editingStudent.parent_name || "");
      setParentPhone(editingStudent.parent_phone || "");
    }
  }, [isOpen, mode, editingStudent]);

  useEffect(() => {
    if (mode !== "edit" || !editingStudent) return;
    setClassId(resolvedEditClassId || "");
  }, [mode, editingStudent, resolvedEditClassId]);

  const resetForm = () => {
    setStudentCode("");
    setFullName(""); setDob(""); setGender(""); setPhone("");
    setEmail(""); setAddress("");
    setClassId(""); setParentName(""); setParentPhone("");
    setSchoolId("");
  };

  const handleSave = async () => {
    if (!fullName.trim()) return showErrorToast("Vui lòng nhập Họ tên học viên");
    if (!gender) return showErrorToast("Vui lòng chọn giới tính.");
    if (studentCode.trim() && !STUDENT_CODE_RE.test(studentCode.trim())) {
      return showErrorToast("Mã học sinh phải gồm đúng 10 chữ số.");
    }
    setLoading(true);
    const payload = {
      student_code: studentCode.trim() || null,
      full_name: fullName,
      dob,
      gender,
      phone,
      email: email.trim() || null,
      address: address.trim() || null,
      class_id: classId || null,
      parent_name: parentName,
      parent_phone: parentPhone,
      ...(mode === "create" ? { status: true } : {}),
    };
    try {
      if (mode === "create") await createStudent(payload);
      else await updateStudent(editingStudent.id, payload);
      showSuccessToast(`${mode === "create" ? "Thêm" : "Cập nhật"} học viên thành công!`);
      onSaved?.(); onClose();
    } catch { showErrorToast("Không thể lưu học viên"); } finally { setLoading(false); }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-gray-900/60 backdrop-blur-[2px]"
            onClick={onClose}
          />

          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="fixed inset-0 z-[120] flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="bg-slate-50 rounded-3xl shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden pointer-events-auto border border-white">
              
              {/* HEADER */}
              <div className="bg-white px-8 py-5 flex justify-between items-center border-b border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-red-600 rounded-2xl flex items-center justify-center shadow-lg shadow-red-200">
                    <UserPlus className="text-white w-7 h-7" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 leading-none">
                      {mode === "create" ? "Thêm học viên mới" : "Cập nhật hồ sơ học viên"}
                    </h2>
                    <p className="text-sm text-gray-400 mt-1">Thông tin cá nhân và lớp học của học viên</p>
                  </div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
                  <X size={24} />
                </button>
              </div>

              {/* BODY: GRID 2 CỘT */}
              <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6 overflow-y-auto max-h-[75vh]">
                
                {/* CỘT TRÁI: HỌC VIÊN + PHỤ HUYNH */}
                <div className="space-y-6">
                  <Section icon={User} title="Học viên" subTitle="Dữ liệu định danh cá nhân">
                    <div className="space-y-4">
                      <div className="relative">
                        <label className="text-[10px] uppercase font-bold text-gray-400 absolute -top-2 left-3 bg-white px-1 z-10">
                          Mã học sinh
                        </label>
                        <input
                          value={studentCode}
                          onChange={(e) => {
                            const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 10);
                            setStudentCode(onlyDigits);
                          }}
                          placeholder="Ví dụ: 8000420195 (không bắt buộc)"
                          inputMode="numeric"
                          maxLength={10}
                          className="w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-red-100 focus:border-red-400 outline-none transition-all border-gray-200 font-medium text-sm text-gray-800"
                        />
                        <p className="text-[11px] text-gray-400 mt-1">
                          Tuỳ chọn. Nếu nhập, phải đúng 10 chữ số.
                        </p>
                      </div>
                      <div className="relative">
                        <label className="text-[10px] uppercase font-bold text-gray-400 absolute -top-2 left-3 bg-white px-1 z-10">
                          Họ và tên <span className="text-red-500">*</span>
                        </label>
                        <input
                          value={fullName}
                          onChange={(e) => setFullName(toTitleCaseVietnamese(e.target.value))}
                          placeholder="NGUYỄN VĂN A"
                          className="w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-red-100 focus:border-red-400 outline-none transition-all border-gray-200 font-bold text-gray-800"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="relative">
                          <label className="text-[10px] uppercase font-bold text-gray-400 absolute -top-2 left-3 bg-white px-1 z-10">Ngày sinh</label>
                          <input type="date" value={dob} onChange={(e) => setDob(e.target.value)}
                            className="w-full border rounded-xl px-4 py-2.5 border-gray-200 outline-none focus:ring-2 focus:ring-red-100 font-medium text-sm" />
                        </div>
                        <div className="relative">
                          <label className="text-[10px] uppercase font-bold text-gray-400 absolute -top-2 left-3 bg-white px-1 z-10">Giới tính <span className="text-red-500">*</span></label>
                          <Select
                            styles={genderSelectStyles}
                            options={genderOptions}
                            value={genderOptions.find((g) => g.value === gender) || null}
                            onChange={(opt) => setGender(opt?.value || "")}
                            isSearchable={false}
                            placeholder="Chọn giới tính"
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            menuPosition="fixed"
                          />
                        </div>
                      </div>
                      <div className="relative">
                        <label className="text-[10px] uppercase font-bold text-gray-400 absolute -top-2 left-3 bg-white px-1 z-10">Số điện thoại</label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input value={phone} onChange={(e) => setPhone(e.target.value)}
                            placeholder="0xxx xxx xxx"
                            className="w-full border rounded-xl pl-9 pr-4 py-2.5 border-gray-200 outline-none focus:ring-2 focus:ring-red-100 font-medium text-sm" />
                        </div>
                      </div>
                      <div className="relative">
                        <label className="text-[10px] uppercase font-bold text-gray-400 absolute -top-2 left-3 bg-white px-1 z-10">Email</label>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="email@example.com"
                          autoComplete="email"
                          className="w-full border rounded-xl px-4 py-2.5 border-gray-200 outline-none focus:ring-2 focus:ring-red-100 font-medium text-sm"
                        />
                      </div>
                      <div className="relative">
                        <label className="text-[10px] uppercase font-bold text-gray-400 absolute -top-2 left-3 bg-white px-1 z-10">Địa chỉ cư trú</label>
                        <div className="relative">
                          <MapPin className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                          <textarea
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            placeholder="Số nhà, đường, phường/xã, tỉnh/thành..."
                            rows={3}
                            className="w-full border rounded-xl pl-9 pr-4 py-2.5 border-gray-200 outline-none focus:ring-2 focus:ring-red-100 font-medium text-sm resize-y min-h-[88px] align-top"
                          />
                        </div>
                      </div>
                    </div>
                  </Section>
                  <Section icon={Heart} title="Phụ huynh" subTitle="Người bảo hộ liên hệ">
                    <div className="space-y-4">
                      <div className="relative">
                        <label className="text-[10px] uppercase font-bold text-gray-400 absolute -top-2 left-3 bg-white px-1 z-10">Tên bố/mẹ</label>
                        <input value={parentName} onChange={(e) => setParentName(e.target.value)} onBlur={(e) => setParentName(toTitleCaseVietnamese(e.target.value))}
                          placeholder="Nhập tên phụ huynh..." className="w-full border rounded-xl px-4 py-3 border-gray-200 outline-none focus:ring-2 focus:ring-red-100 font-medium text-sm" />
                      </div>
                      <div className="relative">
                        <label className="text-[10px] uppercase font-bold text-gray-400 absolute -top-2 left-3 bg-white px-1 z-10">SĐT phụ huynh</label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input value={parentPhone} onChange={(e) => setParentPhone(e.target.value)}
                            placeholder="0xxx xxx xxx" className="w-full border rounded-xl pl-9 pr-4 py-2.5 border-gray-200 outline-none focus:ring-2 focus:ring-red-100 font-medium text-sm" />
                        </div>
                      </div>
                    </div>
                  </Section>
                </div>

                {/* CỘT PHẢI: LỚP HỌC + CẢNH BÁO */}
                <div className="space-y-6">
                  <Section icon={GraduationCap} title="Lớp học" subTitle="Đăng ký học vụ">
                    <div className="space-y-3">
                      {isSuperAdmin ? (
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase font-bold text-gray-400">Chi nhánh</label>
                          <HrmDashboardBranchPicker
                            instanceId="student-form-branch"
                            options={[
                              {
                                value: "",
                                label: "Chọn chi nhánh",
                                sub: "Tên chi nhánh • Loại chi nhánh • Người đứng đầu",
                              },
                              ...branchOptions.map((b) => ({
                                value: b.value,
                                label: b.label,
                                sub: branchSubtitle(b),
                              })),
                            ]}
                            value={branchId || ""}
                            onChange={(nextVal) => {
                              const nextBranchId = nextVal || null;
                              const picked = branchOptions.find((b) => b.value === nextBranchId);
                              const aff = picked?.affiliation_type || "affiliated";
                              setBranchId(nextBranchId);
                              setBranchAffiliation(aff);
                              setUnit(aff === "franchise" ? "center" : "school");
                              setSchoolId("");
                              setClassId("");
                              setClasses([]);
                            }}
                            aria-label="Chọn chi nhánh cho lớp học"
                          />
                        </div>
                      ) : null}

                      {canPickUnit ? (
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase font-bold text-gray-400">
                            Đối tượng
                          </label>
                          <div className="grid grid-cols-2 gap-3 pt-1">
                            <button
                              type="button"
                              onClick={() => {
                                setUnit("school");
                                setSchoolId("");
                                setClassId("");
                                setClasses([]);
                              }}
                              className={`flex flex-col items-center p-3 rounded-2xl border-2 transition-all ${
                                unit === "school"
                                  ? "border-red-600 bg-red-50 text-red-700"
                                  : "border-gray-100 bg-white text-gray-500 hover:border-gray-300"
                              }`}
                              disabled={isFranchiseBranch}
                            >
                              <span className="text-xl mb-1">🏫</span>
                              <span className="text-[11px] font-black uppercase tracking-wider">
                                Tại trường học
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setUnit("center");
                                setSchoolId("");
                                setClassId("");
                                setClasses([]);
                              }}
                              className={`flex flex-col items-center p-3 rounded-2xl border-2 transition-all ${
                                unit === "center"
                                  ? "border-red-600 bg-red-50 text-red-700"
                                  : "border-gray-100 bg-white text-gray-500 hover:border-gray-300"
                              }`}
                            >
                              <span className="text-xl mb-1">🏛️</span>
                              <span className="text-[11px] font-black uppercase tracking-wider">
                                Tại trung tâm
                              </span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-bold text-red-700">
                          Loại chi nhánh nhượng quyền: chỉ áp dụng lớp tại trung tâm.
                        </div>
                      )}

                      {showSchoolSelect ? (
                        <div className="space-y-2">
                          <label className="text-[10px] uppercase font-bold text-gray-400">Đối tác trường học</label>
                          <Select
                            options={schools.map((s) => ({
                              value: s.id,
                              label: s.name,
                              ward: s.ward || s.ward_name || "",
                            }))}
                            value={
                              schools
                                .map((s) => ({ value: s.id, label: s.name, ward: s.ward || s.ward_name || "" }))
                                .find((o) => o.value === schoolId) || null
                            }
                            onChange={(opt) => {
                              setSchoolId(opt?.value || "");
                              setClassId("");
                            }}
                            placeholder="Chọn đối tác trường học"
                            styles={customerInModalSelectStyles}
                            formatOptionLabel={formatCustomerOptionLabel}
                            filterOption={filterCustomerOption}
                            {...modalSelectPortalProps}
                            isDisabled={!branchId}
                            isClearable
                          />
                        </div>
                      ) : null}

                      <div className="space-y-2">
                        <label className="text-[10px] uppercase font-bold text-gray-400 flex items-center gap-2">
                          <GraduationCap size={14} /> Lớp học (tuỳ chọn)
                        </label>
                        <Select
                          instanceId="student-form-class"
                          styles={classSelectStyles}
                          options={classOptions}
                          value={classOptions.find((c) => c.value === classId) || null}
                          onChange={(opt) => setClassId(opt?.value || "")}
                          formatOptionLabel={formatClassOptionLabel}
                          filterOption={filterClassOption}
                          isLoading={classesLoading}
                          isDisabled={
                            classesLoading
                            || (isSuperAdmin && (!branchId || (showSchoolSelect && !schoolId)))
                          }
                          placeholder={
                            classesLoading
                              ? "Đang tải danh sách lớp..."
                              : "Tìm hoặc chọn lớp..."
                          }
                          noOptionsMessage={({ inputValue }) =>
                            inputValue
                              ? "Không tìm thấy lớp phù hợp"
                              : "Không có lớp trong điều kiện hiện tại"
                          }
                          isClearable
                          isSearchable
                          maxMenuHeight={280}
                          menuPlacement="auto"
                          {...classSelectPortalProps}
                        />
                      </div>
                    </div>
                  </Section>

                  {/* CÓ THỂ THÊM GHI CHÚ HOẶC THÔNG TIN BỔ SUNG Ở ĐÂY */}
                  <div className="p-5 bg-blue-50 border border-blue-100 rounded-2xl">
                    <div className="flex gap-3">
                      <ShieldCheck className="text-blue-500 shrink-0" size={20} />
                      <p className="text-xs text-blue-700 leading-relaxed font-medium">
                        Dữ liệu học viên sẽ được bảo mật trong hệ thống ERP. Sau khi lưu, bạn có thể thực hiện điểm danh và thu học phí cho học viên này.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* FOOTER */}
              <div className="bg-white px-8 py-5 flex justify-end gap-4 border-t border-gray-100">
                <button onClick={onClose} className="px-8 py-2.5 rounded-xl font-semibold text-gray-500 hover:bg-gray-50 border border-gray-200 transition-all">
                  Huỷ bỏ
                </button>
                <button onClick={handleSave} disabled={loading}
                  className="px-10 py-2.5 rounded-xl bg-red-600 text-white font-bold flex items-center gap-2 hover:bg-red-700 hover:shadow-lg hover:shadow-red-200 active:scale-95 transition-all disabled:opacity-60"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save size={18} />}
                  {loading ? "Đang xử lý..." : "Hoàn tất & Lưu hồ sơ"}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default StudentFormModal;